import React, { useState, useEffect } from 'react';
import { Search, Filter, Link as LinkIcon, Image as ImageIcon, Plus, X, Trash2, Send, Upload, Eye, ChevronDown, ChevronUp } from 'lucide-react';

// ─── Visual similarity engine ───────────────────────────────────────
const analyzeImageFeatures = (base64) => new Promise((resolve) => {
  const img = new Image();
  img.src = base64;
  img.crossOrigin = 'Anonymous';
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 50; canvas.height = 50;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, 50, 50);
    const data = ctx.getImageData(0, 0, 50, 50).data;
    let r = 0, g = 0, b = 0, count = 0;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] > 128) { r += data[i]; g += data[i + 1]; b += data[i + 2]; count++; }
    }
    resolve(count === 0 ? { r: 255, g: 255, b: 255 } : { r: Math.round(r/count), g: Math.round(g/count), b: Math.round(b/count) });
  };
  img.onerror = () => resolve(null);
});

const calcSimilarity = (f1, f2) => {
  if (!f1 || !f2) return 0;
  const d = Math.sqrt(Math.pow(f1.r-f2.r,2) + Math.pow(f1.g-f2.g,2) + Math.pow(f1.b-f2.b,2));
  return Math.max(0, (1 - d/441) * 100);
};

// ─── Constants ──────────────────────────────────────────────────────
const VA_TYPES = ['Print', 'Embroidery', 'Washing', 'Dyeing', 'Coating', 'Bonding', 'Pleating', 'Smocking', 'Beading', 'Applique', 'Laser', 'Other'];
const UOM_OPTIONS = ['Pcs', 'Mtr', 'Dozen', 'Set', 'Kg'];

const EMPTY_FORM = { name: '', supplier: '', process: 'Print', rate: '', uom: 'Pcs', notes: '', image: '' };

// ─── Main Component ─────────────────────────────────────────────────
export default function VALibrary({ db, setDb, saveToDB }) {

  const [searchTerm,     setSearchTerm]     = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterType,     setFilterType]     = useState('');
  const [searchImage,    setSearchImage]    = useState(null);
  const [scoredLib,      setScoredLib]      = useState([]);
  const [showAddForm,    setShowAddForm]    = useState(false);
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [showReqModal,   setShowReqModal]   = useState(false);
  const [reqSup,         setReqSup]         = useState('');
  const [reqItem,        setReqItem]        = useState('');
  const [viewItem,       setViewItem]       = useState(null);
  const [editId,         setEditId]         = useState(null);

  const suppliers = db.suppliers || [];
  const vaItems   = db.vaLibrary  || [];
  const vaSuppliers = suppliers.filter(s => s.supplierType === 'VA Process Supplier');

  // ── Save library item ──────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) return alert('Enter item name');
    const item = editId ? { ...vaItems.find(x=>x.id===editId), ...form } : { ...form, id: Date.now() };
    const entry = editId
      ? vaItems.map(x => x.id === editId ? item : x)
      : [...vaItems, item];
    setDb(prev => ({ ...prev, vaLibrary: entry }));
    if (saveToDB) {
      const saved = await saveToDB('va_item', item);
      // If server returned a real DB id, update local state
      if (saved?.id && !editId) {
        item.id = saved.id;
        const entryWithId = entry.map(x => x.id === item.id || (!editId && x === item) ? {...x, id: saved.id} : x);
        setDb(prev => ({ ...prev, vaLibrary: entryWithId }));
      }
    }
    setForm(EMPTY_FORM);
    setShowAddForm(false);
    setEditId(null);
  };

  // ── Delete item ────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Remove this item?')) return;
    const updated = vaItems.filter(x => x.id !== id);
    setDb(prev => ({ ...prev, vaLibrary: updated }));
    if (saveToDB) await saveToDB('va_item', { id, _deleted: true });
    if (viewItem?.id === id) setViewItem(null);
  };

  // ── Image upload helper ────────────────────────────────────────────
  const readImage = (file, cb) => {
    const r = new FileReader();
    r.onload = ev => cb(ev.target.result);
    r.readAsDataURL(file);
  };

  // ── Visual search ──────────────────────────────────────────────────
  const handleImageSearch = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    readImage(file, async (src) => {
      setSearchImage(src);
      const feat = await analyzeImageFeatures(src);
      const scored = await Promise.all(vaItems.map(async item => {
        const iFeat = item.image ? await analyzeImageFeatures(item.image) : null;
        return { ...item, score: calcSimilarity(feat, iFeat) };
      }));
      setScoredLib(scored.sort((a, b) => b.score - a.score));
    });
  };

  // ── Build request message ──────────────────────────────────────────
  const buildMsg = (supplier, item) => {
    const itemTxt = item ? ` for: *${item}*` : '';
    return `Dear ${supplier},\n\nWe are updating our VA Library and would like to request product images${itemTxt}.\n\nPlease share:\n• Product / sample images (front, back, detail shots)\n• Process / technique samples\n• Catalogue or lookbook if available\n• Price list / rate card\n\nYou can reply directly to this message with files attached.\n\nThank you,\nSourcing Team`;
  };

  const getSupContact = (name) => suppliers.find(s => s.name === name) || {};

  const sendWhatsApp = (supName, item) => {
    const sup = getSupContact(supName);
    const ph = (sup?.contact?.whatsapp || sup?.contact?.mobile || '').replace(/\D/g, '');
    if (!ph) return alert('No WhatsApp/mobile for ' + supName + '. Update in Registration.');
    window.open('https://wa.me/' + ph + '?text=' + encodeURIComponent(buildMsg(supName, item)), '_blank');
  };

  const sendEmail = (supName, item) => {
    const sup = getSupContact(supName);
    if (!sup?.contact?.email) return alert('No email for ' + supName + '. Update in Registration.');
    const subj = 'VA Library Image Request' + (item ? ' — ' + item : '') + ' | ' + supName;
    window.open('mailto:' + sup.contact.email + '?subject=' + encodeURIComponent(subj) + '&body=' + encodeURIComponent(buildMsg(supName, item)), '_blank');
  };

  // ── Filter ─────────────────────────────────────────────────────────
  const sourceList = searchImage ? scoredLib : vaItems;
  const filtered = sourceList.filter(item => {
    const ms = (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
               (item.notes || '').toLowerCase().includes(searchTerm.toLowerCase());
    const mSup  = !filterSupplier || item.supplier === filterSupplier;
    const mType = !filterType     || item.process  === filterType;
    const mScore = !searchImage   || (item.score || 0) > 50;
    return ms && mSup && mType && mScore;
  });

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display:'flex', height:'calc(100vh - 80px)', background:'#f8fafc', fontFamily:'sans-serif', overflow:'hidden', position:'relative' }}>

      {/* ── SIDEBAR ── */}
      <div style={{ width:'240px', background:'white', borderRight:'1px solid #e2e8f0', padding:'16px', display:'flex', flexDirection:'column', gap:'16px', flexShrink:0 }}>
        <div style={{ fontWeight:'800', color:'#1e293b', fontSize:'13px', display:'flex', alignItems:'center', gap:'6px' }}>
          <Filter size={14}/> FILTERS
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
          <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', textTransform:'uppercase' }}>Supplier</label>
          <select value={filterSupplier} onChange={e=>setFilterSupplier(e.target.value)}
            style={{ padding:'7px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px' }}>
            <option value="">All Suppliers</option>
            {vaSuppliers.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
          <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', textTransform:'uppercase' }}>VA Type</label>
          <select value={filterType} onChange={e=>setFilterType(e.target.value)}
            style={{ padding:'7px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px' }}>
            <option value="">All Types</option>
            {VA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {(filterSupplier || filterType || searchTerm) && (
          <button onClick={()=>{ setFilterSupplier(''); setFilterType(''); setSearchTerm(''); setSearchImage(null); }}
            style={{ padding:'6px', background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:'4px', fontSize:'11px', cursor:'pointer', color:'#64748b' }}>
            Clear Filters
          </button>
        )}

        {/* ── Send image request ── */}
        <div style={{ marginTop:'auto', borderTop:'1px solid #f1f5f9', paddingTop:'14px', display:'flex', flexDirection:'column', gap:'8px' }}>
          <div style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', marginBottom:'2px' }}>📤 Request Images</div>
          <select value={reqSup} onChange={e=>setReqSup(e.target.value)}
            style={{ padding:'7px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px' }}>
            <option value="">Select Supplier</option>
            {vaSuppliers.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
          <input value={reqItem} onChange={e=>setReqItem(e.target.value)} placeholder="Item / process name (optional)"
            style={{ padding:'7px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px' }}/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
            <button onClick={()=>{ if(!reqSup) return alert('Select a supplier'); sendWhatsApp(reqSup, reqItem); }}
              style={{ padding:'7px', background:'#16a34a', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'600' }}>
              💬 WhatsApp
            </button>
            <button onClick={()=>{ if(!reqSup) return alert('Select a supplier'); sendEmail(reqSup, reqItem); }}
              style={{ padding:'7px', background:'#2563eb', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'11px', fontWeight:'600' }}>
              ✉ Email
            </button>
          </div>
          {reqSup && (()=>{
            const sup = getSupContact(reqSup);
            const hasWA = !!(sup?.contact?.whatsapp || sup?.contact?.mobile);
            const hasEM = !!sup?.contact?.email;
            return (
              <div style={{ fontSize:'10px', color:'#64748b', lineHeight:1.5 }}>
                {hasWA ? <span style={{ color:'#16a34a' }}>📱 WhatsApp ready </span> : <span style={{ color:'#dc2626' }}>⚠ No WhatsApp </span>}
                {hasEM ? <span style={{ color:'#2563eb' }}>✉ Email ready</span> : <span style={{ color:'#dc2626' }}>⚠ No Email</span>}
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── MAIN AREA ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>

        {/* Toolbar */}
        <div style={{ padding:'12px 20px', background:'white', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'12px' }}>
          <div style={{ fontWeight:'800', color:'#334155', fontSize:'14px' }}>
            VA LIBRARY
            <span style={{ color:'#94a3b8', fontSize:'11px', fontWeight:'500', marginLeft:'8px' }}>{filtered.length} items</span>
          </div>

          {/* Search */}
          <div style={{ display:'flex', alignItems:'center', gap:'8px', background:'#f1f5f9', padding:'7px 14px', borderRadius:'20px', flex:1, maxWidth:'320px' }}>
            {searchImage
              ? <img src={searchImage} style={{ width:20, height:20, borderRadius:3, objectFit:'cover', cursor:'pointer' }} onClick={()=>setSearchImage(null)} title="Clear visual search"/>
              : <Search size={14} color="#94a3b8"/>
            }
            <input placeholder="Search by name or notes..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)}
              style={{ border:'none', background:'transparent', outline:'none', fontSize:'12px', flex:1 }}/>
            <label style={{ cursor:'pointer', display:'flex', alignItems:'center' }} title="Search by image">
              <ImageIcon size={14} color="#64748b"/>
              <input type="file" hidden accept="image/*" onChange={handleImageSearch}/>
            </label>
          </div>

          {/* Add button */}
          <button onClick={()=>{ setForm(EMPTY_FORM); setEditId(null); setShowAddForm(v=>!v); }}
            style={{ padding:'8px 16px', background:'#1e293b', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'700', fontSize:'12px', display:'flex', alignItems:'center', gap:'6px' }}>
            <Plus size={14}/> Add Item
          </button>
        </div>

        {/* Add / Edit Form (collapsible) */}
        {showAddForm && (
          <div style={{ background:'white', borderBottom:'2px solid #2563eb', padding:'16px 20px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr auto', gap:'10px', alignItems:'end' }}>
              <div>
                <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>ITEM NAME *</label>
                <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="e.g. Floral Embroidery — Medium Density"
                  style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px', boxSizing:'border-box' }}/>
              </div>
              <div>
                <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>SUPPLIER</label>
                <select value={form.supplier} onChange={e=>setForm({...form,supplier:e.target.value})}
                  style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px' }}>
                  <option value="">—</option>
                  {vaSuppliers.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>PROCESS</label>
                <select value={form.process} onChange={e=>setForm({...form,process:e.target.value})}
                  style={{ width:'100%', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px' }}>
                  {VA_TYPES.map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>RATE</label>
                <div style={{ display:'flex', gap:'4px' }}>
                  <input value={form.rate} onChange={e=>setForm({...form,rate:e.target.value})} placeholder="0.00"
                    style={{ width:'70px', padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px' }}/>
                  <select value={form.uom} onChange={e=>setForm({...form,uom:e.target.value})}
                    style={{ padding:'8px', border:'1px solid #cbd5e1', borderRadius:'4px', fontSize:'12px' }}>
                    {UOM_OPTIONS.map(u=><option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', display:'block', marginBottom:'4px' }}>IMAGE</label>
                <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
                  {form.image
                    ? <img src={form.image} style={{ width:34, height:34, objectFit:'cover', borderRadius:4, border:'1px solid #e2e8f0' }} onError={e=>e.target.style.display='none'}/>
                    : <div style={{ width:34, height:34, background:'#f1f5f9', borderRadius:4, display:'flex', alignItems:'center', justifyContent:'center' }}><ImageIcon size={14} color="#cbd5e1"/></div>
                  }
                  <label style={{ cursor:'pointer', background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:4, padding:'6px 8px', fontSize:'11px', color:'#334155', fontWeight:'600', display:'flex', alignItems:'center', gap:'4px' }}>
                    <Upload size={12}/> Upload
                    <input type="file" hidden accept="image/*" onChange={e=>{ const f=e.target.files[0]; if(f) readImage(f, src=>setForm({...form,image:src})); }}/>
                  </label>
                </div>
              </div>
              <div style={{ display:'flex', gap:'6px', paddingBottom:'1px' }}>
                <button onClick={handleSave}
                  style={{ padding:'8px 16px', background:'#16a34a', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'700', fontSize:'12px' }}>
                  {editId ? 'Update' : 'Save'}
                </button>
                <button onClick={()=>{ setShowAddForm(false); setEditId(null); setForm(EMPTY_FORM); }}
                  style={{ padding:'8px', background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer' }}>
                  <X size={14} color="#64748b"/>
                </button>
              </div>
            </div>
            <div style={{ marginTop:'8px' }}>
              <input value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Notes / description (optional)"
                style={{ width:'100%', padding:'8px', border:'1px solid #e2e8f0', borderRadius:'4px', fontSize:'12px', boxSizing:'border-box' }}/>
            </div>
          </div>
        )}

        {/* Gallery */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px' }}>
          {filtered.length === 0
            ? <div style={{ textAlign:'center', padding:'60px', color:'#94a3b8' }}>
                <ImageIcon size={40} style={{ marginBottom:'12px', opacity:0.3 }}/>
                <div style={{ fontWeight:'600', fontSize:'14px' }}>No items found</div>
                <div style={{ fontSize:'12px', marginTop:'5px' }}>
                  {vaItems.length === 0
                    ? 'Add your first item using the "+ Add Item" button, or request images from a supplier using the sidebar.'
                    : 'Try adjusting your filters or search term.'}
                </div>
              </div>
            : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'16px' }}>
                {filtered.map(item => (
                  <div key={item.id} style={{ background:'white', border:'1px solid #e2e8f0', borderRadius:'10px', overflow:'hidden', transition:'transform 0.15s, box-shadow 0.15s', cursor:'pointer' }}
                    onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 6px 16px rgba(0,0,0,0.08)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>

                    {/* Image area */}
                    <div style={{ height:160, background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', borderBottom:'1px solid #f1f5f9', position:'relative', overflow:'hidden' }}
                      onClick={()=>setViewItem(item)}>
                      {item.image
                        ? <img src={item.image} alt={item.name} style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} onError={e=>e.target.style.display='none'}/>
                        : <div style={{ textAlign:'center', color:'#cbd5e1' }}><ImageIcon size={32}/><div style={{ fontSize:'10px', marginTop:'4px' }}>No image</div></div>
                      }
                      {searchImage && item.score > 0 && (
                        <div style={{ position:'absolute', top:6, right:6, background:'#1e293b', color:'white', fontSize:'9px', fontWeight:'700', padding:'2px 6px', borderRadius:'8px' }}>
                          {Math.round(item.score)}% match
                        </div>
                      )}
                      {/* Upload overlay */}
                      <label style={{ position:'absolute', bottom:6, right:6, background:'rgba(0,0,0,0.55)', color:'white', borderRadius:'6px', padding:'4px 7px', fontSize:'10px', cursor:'pointer', display:'flex', alignItems:'center', gap:'3px', fontWeight:'600' }}
                        onClick={e=>e.stopPropagation()}>
                        <Upload size={10}/> {item.image ? 'Change' : 'Upload'}
                        <input type="file" hidden accept="image/*" onChange={e=>{
                          const f=e.target.files[0];
                          if(!f) return;
                          readImage(f, async src=>{
                            const updated=vaItems.map(x=>x.id===item.id?{...x,image:src}:x);
                            setDb(prev=>({...prev,vaLibrary:updated}));
                            if(saveToDB) await saveToDB('va_item', {...item, image:src});
                          });
                        }}/>
                      </label>
                    </div>

                    {/* Card body */}
                    <div style={{ padding:'10px 12px' }}>
                      <div style={{ fontSize:'9px', color:'#2563eb', fontWeight:'700', textTransform:'uppercase', marginBottom:'2px' }}>{item.process}</div>
                      <div style={{ fontWeight:'700', fontSize:'13px', color:'#1e293b', marginBottom:'3px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }} title={item.name}>{item.name}</div>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:'11px', color:'#64748b', marginBottom:'8px' }}>
                        <span>{item.supplier || 'Internal'}</span>
                        <span style={{ fontWeight:'700', color:'#16a34a' }}>${item.rate||'—'}/{item.uom||'Pcs'}</span>
                      </div>

                      {/* Action buttons */}
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'4px' }}>
                        <button onClick={()=>{ sendWhatsApp(item.supplier, item.name); }}
                          disabled={!item.supplier}
                          title="Request image via WhatsApp"
                          style={{ padding:'5px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'4px', cursor:item.supplier?'pointer':'not-allowed', fontSize:'12px', opacity:item.supplier?1:0.4 }}>
                          💬
                        </button>
                        <button onClick={()=>{ sendEmail(item.supplier, item.name); }}
                          disabled={!item.supplier}
                          title="Request image via Email"
                          style={{ padding:'5px', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:'4px', cursor:item.supplier?'pointer':'not-allowed', fontSize:'12px', opacity:item.supplier?1:0.4 }}>
                          ✉
                        </button>
                        <button onClick={()=>{ setForm({name:item.name,supplier:item.supplier||'',process:item.process,rate:item.rate||'',uom:item.uom||'Pcs',notes:item.notes||'',image:item.image||''}); setEditId(item.id); setShowAddForm(true); }}
                          title="Edit item"
                          style={{ padding:'5px', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'4px', cursor:'pointer', fontSize:'12px' }}>
                          ✏️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      </div>

      {/* ── ITEM DETAIL MODAL ── */}
      {viewItem && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
          onClick={()=>setViewItem(null)}>
          <div style={{ background:'white', borderRadius:'12px', maxWidth:'640px', width:'100%', overflow:'hidden', boxShadow:'0 25px 60px rgba(0,0,0,0.3)' }}
            onClick={e=>e.stopPropagation()}>
            {/* Modal header */}
            <div style={{ background:'linear-gradient(135deg,#1e293b,#334155)', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontWeight:'800', color:'white', fontSize:'15px' }}>{viewItem.name}</div>
                <div style={{ fontSize:'11px', color:'#94a3b8', marginTop:'2px' }}>{viewItem.process} · {viewItem.supplier||'Internal'}</div>
              </div>
              <button onClick={()=>setViewItem(null)} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'white', cursor:'pointer', borderRadius:'6px', padding:'6px' }}>
                <X size={16}/>
              </button>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0' }}>
              {/* Image */}
              <div style={{ background:'#f8fafc', minHeight:'280px', display:'flex', alignItems:'center', justifyContent:'center', borderRight:'1px solid #e2e8f0', position:'relative' }}>
                {viewItem.image
                  ? <img src={viewItem.image} alt={viewItem.name} style={{ maxWidth:'100%', maxHeight:'280px', objectFit:'contain', padding:'16px' }} onError={e=>e.target.style.display='none'}/>
                  : <div style={{ textAlign:'center', color:'#cbd5e1' }}><ImageIcon size={48}/><div style={{ fontSize:'12px', marginTop:'8px' }}>No image</div></div>
                }
                <label style={{ position:'absolute', bottom:12, left:'50%', transform:'translateX(-50%)', background:'#1e293b', color:'white', borderRadius:'6px', padding:'7px 14px', fontSize:'11px', cursor:'pointer', fontWeight:'600', display:'flex', alignItems:'center', gap:'5px', whiteSpace:'nowrap' }}>
                  <Upload size={12}/> Upload Image
                  <input type="file" hidden accept="image/*" onChange={e=>{
                    const f=e.target.files[0];
                    if(!f) return;
                    readImage(f, async src=>{
                      const updated=vaItems.map(x=>x.id===viewItem.id?{...x,image:src}:x);
                      setDb(prev=>({...prev,vaLibrary:updated}));
                      setViewItem(prev=>({...prev,image:src}));
                      if(saveToDB) await saveToDB('va_item', {...viewItem, image:src});
                    });
                  }}/>
                </label>
              </div>

              {/* Details */}
              <div style={{ padding:'20px' }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
                  {[
                    {label:'Process',   value:viewItem.process},
                    {label:'Supplier',  value:viewItem.supplier||'Internal'},
                    {label:'Rate',      value:viewItem.rate ? '$'+viewItem.rate+'/'+viewItem.uom : '—'},
                    {label:'Added',     value:viewItem.id ? new Date(viewItem.id).toLocaleDateString() : '—'},
                  ].map(f=>(
                    <div key={f.label}>
                      <div style={{ fontSize:'10px', color:'#94a3b8', fontWeight:'700', textTransform:'uppercase', marginBottom:'3px' }}>{f.label}</div>
                      <div style={{ fontWeight:'600', color:'#1e293b', fontSize:'13px' }}>{f.value}</div>
                    </div>
                  ))}
                </div>
                {viewItem.notes && (
                  <div style={{ background:'#f8fafc', padding:'10px', borderRadius:'6px', fontSize:'12px', color:'#334155', marginBottom:'16px', border:'1px solid #e2e8f0' }}>
                    {viewItem.notes}
                  </div>
                )}

                {viewItem.supplier && (
                  <div style={{ borderTop:'1px solid #f1f5f9', paddingTop:'14px' }}>
                    <div style={{ fontSize:'10px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', marginBottom:'8px' }}>📤 Request Image from Supplier</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                      <button onClick={()=>sendWhatsApp(viewItem.supplier, viewItem.name)}
                        style={{ padding:'9px', background:'#16a34a', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'700', fontSize:'12px' }}>
                        💬 WhatsApp
                      </button>
                      <button onClick={()=>sendEmail(viewItem.supplier, viewItem.name)}
                        style={{ padding:'9px', background:'#2563eb', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'700', fontSize:'12px' }}>
                        ✉ Email
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ marginTop:'12px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                  <button onClick={()=>{ setForm({name:viewItem.name,supplier:viewItem.supplier||'',process:viewItem.process,rate:viewItem.rate||'',uom:viewItem.uom||'Pcs',notes:viewItem.notes||'',image:viewItem.image||''}); setEditId(viewItem.id); setShowAddForm(true); setViewItem(null); }}
                    style={{ padding:'8px', background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:'6px', cursor:'pointer', fontWeight:'600', fontSize:'12px' }}>
                    ✏️ Edit
                  </button>
                  <button onClick={()=>handleDelete(viewItem.id)}
                    style={{ padding:'8px', background:'#fef2f2', border:'1px solid #fecaca', color:'#dc2626', borderRadius:'6px', cursor:'pointer', fontWeight:'600', fontSize:'12px' }}>
                    🗑 Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
